"use client";

import { getPhoenixTraderSubaccountAddress } from "@ellipsis-labs/rise";
import { address } from "@solana/kit";

import { asAuthority } from "@/lib/rise/accountFetcher";
import { phoenixProgramAddress } from "@/lib/rise/programAddress";
import { getSolanaRpc } from "@/lib/tx/rpc";
import {
  formatLamportsAsSol,
  SOLANA_LAMPORTS_PER_SIGNATURE,
} from "@/lib/tx/solanaFunding";

export { formatLamportsAsSol };

export const TRADER_REGISTRATION_MAX_POSITIONS = 32;
export const TRADER_ACCOUNT_HEADER_BYTES = 224;
export const TRADER_POSITION_MAP_HEADER_BYTES = 16;
export const TRADER_POSITION_ENTRY_BYTES = 40;
export const TRADER_REGISTRATION_LAMPORTS_PER_SIGNATURE =
  SOLANA_LAMPORTS_PER_SIGNATURE;
export const TRADER_REGISTRATION_SIGNATURE_COUNT = 2n;
export const TRADER_REGISTRATION_TX_FEE_LAMPORTS =
  TRADER_REGISTRATION_LAMPORTS_PER_SIGNATURE *
  TRADER_REGISTRATION_SIGNATURE_COUNT;

export const getTraderAccountBytes = (
  maxPositions = TRADER_REGISTRATION_MAX_POSITIONS
): number =>
  TRADER_ACCOUNT_HEADER_BYTES +
  TRADER_POSITION_MAP_HEADER_BYTES +
  maxPositions * TRADER_POSITION_ENTRY_BYTES;

export const TRADER_REGISTRATION_ACCOUNT_BYTES = getTraderAccountBytes();

export interface TraderRegistrationFundingStatus {
  balanceLamports: bigint;
  canFundNewTrader: boolean;
  maxPositions: number;
  requiredFundingLamports: bigint;
  requiredRentLamports: bigint;
  requiresNewTraderAccount: boolean;
  shortfallLamports: bigint;
  traderAccountBytes: number;
  traderAccount: string;
  txFeeLamports: bigint;
  walletHasRequiredLamports: boolean;
}

export class InsufficientTraderRentError extends Error {
  override readonly name = "InsufficientTraderRentError";
}

export const getInsufficientTraderRentMessage = (
  status: TraderRegistrationFundingStatus
): string | null => {
  if (status.canFundNewTrader) return null;
  return `This wallet has ${formatLamportsAsSol(
    status.balanceLamports
  )} SOL, but a new Phoenix trader requires ${formatLamportsAsSol(
    status.requiredFundingLamports
  )} SOL for rent and transaction fees. Add at least ${formatLamportsAsSol(
    status.shortfallLamports
  )} SOL and try again.`;
};

export const getTraderRegistrationFundingStatus = async (
  authority: string
): Promise<TraderRegistrationFundingStatus> => {
  const rpc = getSolanaRpc();
  const traderAccount = await getPhoenixTraderSubaccountAddress({
    authority: asAuthority(authority),
    traderPdaIndex: 0,
    subaccountIndex: 0,
    phoenixProgramAddress: phoenixProgramAddress(),
  });

  const [requiredRentLamports, balance, traderAccountInfo] = await Promise.all([
    rpc
      .getMinimumBalanceForRentExemption(
        BigInt(TRADER_REGISTRATION_ACCOUNT_BYTES),
        {
          commitment: "confirmed",
        }
      )
      .send(),
    rpc.getBalance(address(authority), { commitment: "confirmed" }).send(),
    rpc
      .getAccountInfo(traderAccount, {
        commitment: "confirmed",
        encoding: "base64",
      })
      .send(),
  ]);

  const balanceLamports = balance.value;
  const requiresNewTraderAccount = traderAccountInfo.value === null;
  const requiredFundingLamports =
    requiredRentLamports + TRADER_REGISTRATION_TX_FEE_LAMPORTS;
  const walletHasRequiredLamports = balanceLamports >= requiredFundingLamports;
  const canFundNewTrader =
    !requiresNewTraderAccount || walletHasRequiredLamports;
  const shortfallLamports = walletHasRequiredLamports
    ? 0n
    : requiredFundingLamports - balanceLamports;

  return {
    balanceLamports,
    canFundNewTrader,
    maxPositions: TRADER_REGISTRATION_MAX_POSITIONS,
    requiredFundingLamports,
    requiredRentLamports,
    requiresNewTraderAccount,
    shortfallLamports,
    traderAccount: String(traderAccount),
    traderAccountBytes: TRADER_REGISTRATION_ACCOUNT_BYTES,
    txFeeLamports: TRADER_REGISTRATION_TX_FEE_LAMPORTS,
    walletHasRequiredLamports,
  };
};

export const assertCanFundTraderRegistration = async (
  authority: string
): Promise<void> => {
  const status = await getTraderRegistrationFundingStatus(authority);
  const message = getInsufficientTraderRentMessage(status);
  if (message !== null) {
    throw new InsufficientTraderRentError(message);
  }
};
